import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { Crop, RotateCcw } from 'lucide-react'

interface ProductImageCropperProps {
  file: File
  onCancel: () => void
  onConfirm: (file: File) => void
  disabled?: boolean
}

type Selection = { x: number; y: number; width: number; height: number }
type Handle = 'move' | 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'
const handles: Exclude<Handle, 'move'>[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
const handleNames = { nw: 'coin supérieur gauche', n: 'bord supérieur', ne: 'coin supérieur droit', e: 'bord droit', se: 'coin inférieur droit', s: 'bord inférieur', sw: 'coin inférieur gauche', w: 'bord gauche' }
const fullSelection: Selection = { x: 0, y: 0, width: 1, height: 1 }
const outputWidth = 1600
const outputHeight = 1200
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

// Coordinates are fractions of the source image, independent of screen size.
const adjustSelection = (selection: Selection, handle: Handle, dx: number, dy: number, minWidth: number, minHeight: number): Selection => {
  if (handle === 'move') return { ...selection, x: clamp(selection.x + dx, 0, 1 - selection.width), y: clamp(selection.y + dy, 0, 1 - selection.height) }
  let left = selection.x
  let right = left + selection.width
  let top = selection.y
  let bottom = top + selection.height
  if (handle.includes('w')) left = clamp(left + dx, 0, right - minWidth)
  if (handle.includes('e')) right = clamp(right + dx, left + minWidth, 1)
  if (handle.includes('n')) top = clamp(top + dy, 0, bottom - minHeight)
  if (handle.includes('s')) bottom = clamp(bottom + dy, top + minHeight, 1)
  return { x: left, y: top, width: right - left, height: bottom - top }
}

const ProductImageCropper = ({ file, onCancel, onConfirm, disabled = false }: ProductImageCropperProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ pointerId: number; x: number; y: number; handle: Handle; selection: Selection } | null>(null)
  const [image, setImage] = useState<HTMLImageElement | null>(null)
  const [selection, setSelection] = useState<Selection>(fullSelection)
  const [error, setError] = useState('')
  const [exporting, setExporting] = useState(false)
  const busy = disabled || exporting
  const generationRef = useRef(0)

  useEffect(() => {
    const generation = ++generationRef.current
    const imageUrl = URL.createObjectURL(file)
    const source = new Image()
    setImage(null)
    setSelection(fullSelection)
    setError('')
    setExporting(false)
    source.onload = () => { if (generationRef.current === generation) setImage(source) }
    source.onerror = () => setError('Impossible de charger cette image. Choisissez un autre fichier.')
    source.src = imageUrl
    return () => {
      generationRef.current = generation + 1
      source.onload = null
      source.onerror = null
      URL.revokeObjectURL(imageUrl)
    }
  }, [file])

  useEffect(() => {
    const context = canvasRef.current?.getContext('2d')
    if (!context || !image) return
    const sourceWidth = selection.width * image.naturalWidth
    const sourceHeight = selection.height * image.naturalHeight
    const scale = Math.min(outputWidth / sourceWidth, outputHeight / sourceHeight)
    const width = sourceWidth * scale
    const height = sourceHeight * scale
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, outputWidth, outputHeight)
    context.drawImage(image, selection.x * image.naturalWidth, selection.y * image.naturalHeight,
      sourceWidth, sourceHeight, (outputWidth - width) / 2, (outputHeight - height) / 2, width, height)
  }, [image, selection])

  const adjust = (original: Selection, handle: Handle, dx: number, dy: number) => {
    if (!image) return
    setSelection(adjustSelection(original, handle, dx, dy, Math.min(1, 16 / image.naturalWidth), Math.min(1, 16 / image.naturalHeight)))
  }
  const startDrag = (event: PointerEvent<HTMLElement>, handle: Handle) => {
    if (busy || !image || event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.focus()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, handle, selection }
  }
  const drag = (event: PointerEvent<HTMLElement>) => {
    const active = dragRef.current
    const bounds = stageRef.current?.getBoundingClientRect()
    if (!active || active.pointerId !== event.pointerId || !bounds || busy) return
    adjust(active.selection, active.handle, (event.clientX - active.x) / bounds.width, (event.clientY - active.y) / bounds.height)
  }
  const keyboardAdjust = (event: KeyboardEvent<HTMLElement>, handle: Handle) => {
    const directions: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }
    const direction = directions[event.key]
    if (!direction || busy || !image) return
    event.preventDefault()
    event.stopPropagation()
    const step = event.shiftKey ? 10 : 1
    adjust(selection, handle, direction[0] * step / image.naturalWidth, direction[1] * step / image.naturalHeight)
  }
  const confirmCrop = () => {
    const canvas = canvasRef.current
    if (!canvas || !image || busy) return
    const generation = generationRef.current
    setExporting(true)
    setError('')
    try {
      canvas.toBlob(blob => {
        if (generationRef.current !== generation) return
        setExporting(false)
        if (!blob || blob.type !== 'image/webp') {
          setError('Impossible d’exporter l’image en WebP. Réessayez avec un navigateur compatible.')
          return
        }
        const baseName = file.name.replace(/\.[^.]+$/, '') || 'produit'
        onConfirm(new File([blob], `${baseName}-recadree.webp`, { type: 'image/webp', lastModified: Date.now() }))
      }, 'image/webp', 0.92)
    } catch {
      setExporting(false)
      setError('Impossible d’exporter l’image. Veuillez réessayer.')
    }
  }

  return (
    <div className="product-cropper">
      <p id="crop-instructions" className="product-cropper-help">Déplacez le rectangle sur l’image et tirez ses poignées pour choisir librement la zone à garder. Au clavier : flèches pour ajuster, Maj pour accélérer.</p>
      {error && <p role="alert" className="event-notice event-notice-danger">{error}</p>}
      {!image && !error && <p role="status">Préparation de l’image…</p>}
      {image && <>
        <div className="product-cropper-source">
          <div ref={stageRef} className="product-cropper-stage" style={{ aspectRatio: `${image.naturalWidth} / ${image.naturalHeight}`, width: `min(100%, ${420 * image.naturalWidth / image.naturalHeight}px)` }}
            onPointerMove={drag} onPointerUp={() => { dragRef.current = null }} onPointerCancel={() => { dragRef.current = null }} onLostPointerCapture={() => { dragRef.current = null }}>
            <img src={image.src} alt="Image originale à recadrer" draggable={false} />
            {[
              { left: 0, top: 0, width: 1, height: selection.y },
              { left: 0, top: selection.y + selection.height, width: 1, height: 1 - selection.y - selection.height },
              { left: 0, top: selection.y, width: selection.x, height: selection.height },
              { left: selection.x + selection.width, top: selection.y, width: 1 - selection.x - selection.width, height: selection.height },
            ].map((area, index) => <span key={index} aria-hidden="true" className="product-cropper-shade" style={{ left: `${area.left * 100}%`, top: `${area.top * 100}%`, width: `${area.width * 100}%`, height: `${area.height * 100}%` }} />)}
            <div className="product-cropper-selection" style={{ left: `${selection.x * 100}%`, top: `${selection.y * 100}%`, width: `${selection.width * 100}%`, height: `${selection.height * 100}%` }}>
              <button type="button" className="product-cropper-move" aria-label="Déplacer la zone de recadrage" aria-describedby="crop-instructions" disabled={busy}
                onPointerDown={event => startDrag(event, 'move')} onKeyDown={event => keyboardAdjust(event, 'move')} />
              {handles.map(handle => <button key={handle} type="button" className={`product-cropper-handle handle-${handle}`} disabled={busy}
                aria-label={`Redimensionner : ${handleNames[handle]}`} aria-describedby="crop-instructions"
                onPointerDown={event => startDrag(event, handle)} onKeyDown={event => keyboardAdjust(event, handle)} />)}
            </div>
          </div>
        </div>
        <button type="button" className="btn-secondary justify-self-start" disabled={busy} onClick={() => setSelection(fullSelection)}><RotateCcw className="h-4 w-4" /> Toute l’image</button>
      </>}
      <div>
        <p className="product-cropper-help mb-2">Aperçu final — la sélection est centrée sur un fond blanc, sans déformation.</p>
        <div className="product-cropper-preview">
          <canvas ref={canvasRef} width={outputWidth} height={outputHeight} aria-label="Aperçu de l’image recadrée sur fond blanc" />
        </div>
      </div>
      <div className="product-cropper-actions">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>Annuler</button>
        <button type="button" className="btn-primary" onClick={confirmCrop} disabled={busy || !image}>
          <Crop className="h-4 w-4" /> {busy ? 'Import en cours…' : 'Recadrer et importer'}
        </button>
      </div>
    </div>
  )
}

export default ProductImageCropper
